import { createClient } from '@supabase/supabase-js';
import type { PostgrestError } from '@supabase/supabase-js';

// Public anon key — safe to ship in the bundle. RLS + the veritas-ingest
// Edge Function are what actually protect the data.
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Copy .env.example to .env.local.',
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
});

// Convenience handle pinned to the veritas schema. PostgREST exposes the
// `veritas` schema only after you add it to API → Exposed schemas in the
// project settings (one-time). See README.
export const veritas = supabase.schema('veritas');

// ════════════════════════════════════════════════════════════════════════
// fetchAllPages — the ONLY correct way to read a table that can grow past
// 1000 rows. PostgREST enforces a hard server-side row ceiling
// (`db-max-rows`, 1000 on this project). A `.limit(n)` with n above that
// ceiling is a SILENT no-op, and a query with NO `.limit()` at all is just
// as silently capped — the server returns the first 1000 rows and no error.
// Veritas is a truth product: any read that can exceed 1000 rows MUST go
// through this helper, never a bare `.select()`.
//
// `makeRangedQuery` must return a FRESH builder on every call, already
// carrying the caller's `.eq()/.order()` filters AND `.range(from,to)` — a
// builder can only be awaited once.
//
// CONTRACT: the query's ORDER BY must be a TOTAL order — end it with a
// column (or column set) that is unique within the query's scope (id,
// deal_ticket, position_ticket, credential_id, …). Offset pagination over a
// non-unique sort duplicates or skips rows at every page boundary that lands
// inside a tie cluster. A unique trailing key makes page N and N+1 never
// overlap or gap. See tasks/lessons.md L11.
// ════════════════════════════════════════════════════════════════════════
export const PAGE_SIZE = 1000;

export async function fetchAllPages<T>(
  makeRangedQuery: (
    from: number,
    to: number,
  ) => PromiseLike<{ data: T[] | null; error: PostgrestError | null }>,
): Promise<{ data: T[]; error: PostgrestError | null }> {
  const all: T[] = [];
  let from = 0;
  // Safety ceiling — 500 pages = 500k rows. A real per-account/per-user read
  // never approaches this; the bound just guarantees the loop is finite if
  // something upstream ever keeps returning full pages.
  for (let page = 0; page < 500; page++) {
    const { data, error } = await makeRangedQuery(from, from + PAGE_SIZE - 1);
    if (error) return { data: [], error };
    const rows = data ?? [];
    all.push(...rows);
    if (rows.length < PAGE_SIZE) return { data: all, error: null };
    from += PAGE_SIZE;
  }
  return { data: all, error: null };
}
